import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "404",
  "slug": "robinson-projection-explained",
  "title": "What Is the Robinson Projection? The Map That Tried to Please Everyone",
  "excerpt": "The Robinson projection was designed to look right rather than be right. Here is how a deliberate compromise became one of the most familiar world maps of the 20th century.",
  "date": "2026-06-11",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwd2FsbCUyMHdvcmxkJTIwbWFwfGVufDF8MHx8fDE3ODExNjU1NDF8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The Robinson projection, created by Arthur H. Robinson in 1963, is a compromise world map: it is neither equal-area nor conformal but minimizes overall distortion so the whole planet simply looks \"right.\" National Geographic used it from 1988 to 1998 before switching to the Winkel Tripel.",
  "content": [
    {
      "type": "paragraph",
      "text": "Most map projections start with mathematics. A cartographer decides which property matters most — equal area, true shape, accurate direction — and the formula follows from that choice. The Robinson projection did the opposite. Its creator started with how the map should look and worked backwards to the numbers. The result is one of the most recognisable world maps of the last sixty years, and a fascinating case study in the idea that a map can be useful precisely because it is honest about being a compromise."
    },
    {
      "type": "heading",
      "text": "Who made it, and why"
    },
    {
      "type": "paragraph",
      "text": "Arthur H. Robinson, a geography professor at the University of Wisconsin, designed the projection in 1963 at the request of the Rand McNally map company. They wanted a world map that ordinary readers would find pleasant and uncontroversial — not a specialist tool, but a general-purpose picture of the planet for atlases and classrooms. Robinson's approach was unusual: instead of deriving the map from a single equation, he adjusted the appearance by eye until it looked balanced, then recorded the coordinates in a table. It is sometimes called an 'artistic' or tabular projection for exactly this reason."
    },
    {
      "type": "heading",
      "text": "What it actually does"
    },
    {
      "type": "paragraph",
      "text": "The Robinson projection is a pseudocylindrical map. The equator and all the parallels are straight horizontal lines, while the meridians curve gently outward from a straight central meridian, giving the map its distinctive rounded edges. Crucially, the poles are not points but lines — the top and bottom of the map are wide rather than pinched — which is what stops the high latitudes from being stretched into the monstrous shapes you see on a Mercator map."
    },
    {
      "type": "heading",
      "text": "The trade-off at its heart"
    },
    {
      "type": "paragraph",
      "text": "Here is the key thing to understand: the Robinson projection is neither equal-area nor conformal. It does not preserve sizes correctly, and it does not preserve shapes correctly. What it does is keep both kinds of distortion modest across most of the map, especially in the mid-latitudes where most people and most countries live. No single part of the map is exactly right, but no part is badly wrong either. That is the whole philosophy: spread the error around so that nothing screams for attention."
    },
    {
      "type": "list",
      "items": [
        "Sizes: roughly accurate between about 45° North and South, exaggerated toward the poles but far less than Mercator.",
        "Shapes: visually natural in the middle of the map, increasingly squashed near the top and bottom.",
        "Distances and directions: not preserved exactly anywhere — this is not a navigation map."
      ]
    },
    {
      "type": "heading",
      "text": "Its moment of fame"
    },
    {
      "type": "paragraph",
      "text": "The Robinson projection's biggest endorsement came in 1988, when National Geographic adopted it as the standard projection for its world maps, replacing the older Van der Grinten projection. For a decade it was the face of the world for millions of readers. Then in 1998 the Society switched again, this time to the Winkel Tripel projection, which manages slightly lower distortion overall. The Robinson map did not disappear — it remains common in atlases and classrooms — but its decade as the National Geographic standard is what cemented it in the public eye."
    },
    {
      "type": "heading",
      "text": "How to recognise a Robinson map"
    },
    {
      "type": "paragraph",
      "text": "Once you know the tells, the Robinson projection is easy to pick out of a lineup. Look for a world map that is wider than it is tall in a comfortable, slightly squashed way, with a flat top and bottom rather than pointed poles. The sides bow outward in a gentle curve, and the lines of longitude fan out symmetrically from a straight central meridian. Antarctica and Greenland look large but not absurdly so — nothing like the bloated monsters of a Mercator map. If a map feels familiar and 'normal' without obviously favouring either size or shape, there is a good chance you are looking at a Robinson or its close cousin the Winkel Tripel."
    },
    {
      "type": "heading",
      "text": "Why a compromise can be the right call"
    },
    {
      "type": "paragraph",
      "text": "It is tempting to think a map that is not 'correct' in any single way must be inferior. But for a general-reference world map, the goal is not navigation or area comparison — it is giving a reader an intuitive, undistorted-feeling picture of the whole planet at a glance. For that job, a balanced compromise often beats a mathematically pure projection that is perfect in one dimension and grotesque in another. The Robinson map is a reminder that 'good enough everywhere' can be more useful than 'perfect in one place.'"
    }
  ]
};
